import { useCallback, useEffect, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "lucide-react";
import { CHAPTERS } from "@/lib/sections";
import { useMode } from "@/lib/mode";
import { ChapterStage } from "@/components/presentation/ChapterStage";
import { ProgressDots } from "@/components/presentation/ProgressDots";
import { EndingTransition } from "@/components/presentation/EndingTransition";
import { cn } from "@/lib/cn";

// Internal "page" beyond the last chapter — the polished handoff to Explore.
const TRANSITION_INDEX = CHAPTERS.length;

/**
 * Presentation Mode controller.
 *
 * Owns one piece of state: which chapter is on stage. Renders no nav chrome —
 * no top bar, no sidebar, no slide numbers. Only the chapter, a subtle
 * progress rail, and minimal Back / Continue affordances that fade in when
 * the cursor moves and recede otherwise.
 *
 * Keyboard:
 *   ←  / PageUp   — previous
 *   → / Space / PageDown / Enter — next
 *   Home / End — jump to ends
 *   Esc — exit (handled globally)
 *
 * Chapters with internal state (e.g. the Opening's progressive reveals)
 * register an interceptor via `interceptorRef`. PresentationView consults the
 * interceptor before stepping chapters — if it returns true the chapter
 * handled the gesture internally; otherwise we advance to the next chapter.
 */
export const PresentationView = () => {
  const { exitPresent } = useMode();
  const [index, setIndex] = useState(0);
  const [chromeVisible, setChromeVisible] = useState(true);
  const interceptorRef = useRef(null);

  const atEnd = index >= TRANSITION_INDEX;

  const go = useCallback(
    (next) => {
      const clamped = Math.max(0, Math.min(TRANSITION_INDEX, next));
      setIndex(clamped);
    },
    []
  );

  const goNext = useCallback(() => {
    if (interceptorRef.current?.tryAdvance?.()) return;
    go(index + 1);
  }, [go, index]);
  const goPrev = useCallback(() => {
    if (interceptorRef.current?.tryRetreat?.()) return;
    go(index - 1);
  }, [go, index]);

  // Context passed down to every chapter rendered by the engine. Chapters
  // can hook into Continue / hand-off-to-Explore without owning navigation
  // state themselves.
  const chapterCtx = {
    onContinue: goNext,
    onEnterExplore: exitPresent,
  };

  // Keyboard navigation.
  useEffect(() => {
    const handler = (e) => {
      const tag = (e.target?.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || e.target?.isContentEditable) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // "Enter" key on the ending screen is captured by EndingTransition itself.
      if (atEnd && (e.key === "Enter" || e.key === " ")) return;

      if (e.key === "ArrowRight" || e.key === "PageDown" || e.key === " " || e.key === "Enter") {
        e.preventDefault();
        goNext();
      } else if (e.key === "ArrowLeft" || e.key === "PageUp") {
        e.preventDefault();
        goPrev();
      } else if (e.key === "Home") {
        e.preventDefault();
        go(0);
      } else if (e.key === "End") {
        e.preventDefault();
        go(CHAPTERS.length - 1);
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [go, goNext, goPrev, atEnd]);

  // Auto-hide ambient chrome (Back/Continue) after inactivity for a calmer feel.
  useEffect(() => {
    let timer;
    const reveal = () => {
      setChromeVisible(true);
      clearTimeout(timer);
      timer = setTimeout(() => setChromeVisible(false), 2800);
    };
    reveal();
    window.addEventListener("mousemove", reveal);
    window.addEventListener("keydown", reveal);
    window.addEventListener("touchstart", reveal);
    return () => {
      clearTimeout(timer);
      window.removeEventListener("mousemove", reveal);
      window.removeEventListener("keydown", reveal);
      window.removeEventListener("touchstart", reveal);
    };
  }, []);

  const chapter = atEnd ? null : CHAPTERS[index];

  return (
    <div
      data-testid="presentation-view"
      className="fixed inset-0 z-50 flex flex-col bg-background"
    >
      {/* Exit affordance — a single ghost button in the corner. */}
      <div
        className={cn(
          "absolute right-6 top-6 z-10 transition-opacity duration-500",
          chromeVisible ? "opacity-100" : "opacity-0"
        )}
      >
        <button
          type="button"
          onClick={exitPresent}
          data-testid="exit-presentation-button"
          aria-label="Exit presentation"
          className="inline-flex h-8 items-center gap-1.5 rounded-md border border-border bg-background/80 px-3 text-xs font-medium text-muted-foreground backdrop-blur-md transition-colors hover:bg-surface hover:text-foreground focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        >
          <X className="h-3.5 w-3.5" />
          Exit
          <kbd className="ml-0.5 rounded border border-border bg-surface px-1 py-0.5 text-[10px] font-medium text-muted-foreground">
            Esc
          </kbd>
        </button>
      </div>

      {/* Stage */}
      <main
        data-testid="presentation-stage"
        className="flex-1 overflow-auto"
      >
        <div className="flex min-h-full items-center px-6 pb-32 pt-20 sm:px-10 sm:pb-40 sm:pt-24">
          {atEnd ? (
            <div className="mx-auto w-full">
              <EndingTransition />
            </div>
          ) : (
            <ChapterStage chapter={chapter} interceptorRef={interceptorRef} ctx={chapterCtx} />
          )}
        </div>
      </main>

      {/* Bottom chrome — Back · progress · Continue, clustered at center.
          Hidden entirely during the Opening so the first screen carries no
          "Chapter 1 of N" feel. The chapter-level cluster returns the moment
          the investor advances into the first numbered chapter. */}
      {!atEnd && chapter?.custom !== "opening" && (
        <div className="pointer-events-none absolute inset-x-0 bottom-8 z-10 flex justify-center sm:bottom-10">
          <div
            className={cn(
              "pointer-events-auto inline-flex items-center gap-6 transition-opacity duration-500",
              chromeVisible ? "opacity-100" : "opacity-40"
            )}
          >
            <button
              type="button"
              onClick={goPrev}
              disabled={index === 0}
              data-testid="presentation-prev-button"
              aria-label="Previous chapter"
              className={cn(
                "inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                "disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:text-muted-foreground"
              )}
            >
              <ChevronLeft className="h-3.5 w-3.5" />
              Back
            </button>

            <ProgressDots
              count={CHAPTERS.length}
              index={index}
              onJump={(i) => go(i)}
            />

            <button
              type="button"
              onClick={goNext}
              data-testid="presentation-next-button"
              aria-label="Continue"
              className="inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-xs font-medium text-foreground transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
            >
              Continue
              <ChevronRight className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default PresentationView;
